"use client";

import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface StatData {
  value: number;
  suffix: string;
  label: string;
}
const stats: StatData[] = [{
  value: 150,
  suffix: "+",
  label: "Websites Gelanceerd"
}, {
  value: 99,
  suffix: ".9%",
  label: "Uptime Garantie"
}, {
  value: 24,
  suffix: "/7",
  label: "Premium Support"
}];
function AnimatedCounter({
  value,
  suffix = "",
  isVisible
}: {
  value: number;
  suffix?: string;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let animationFrameId: number;
    const duration = 2500;
    const startTime = performance.now();
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * value));
      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        setCount(value);
      }
    };
    animationFrameId = requestAnimationFrame(animate);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isVisible, value]);
  return <span>
      {count}
      {suffix}
    </span>;
}
function StatItem({
  stat,
  index
}: {
  stat: StatData;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5,
    once: true
  });
  return <div data-section-id="147641" ref={ref} data-index={index} className="flex flex-col items-center justify-center space-y-6 p-6 transition-all duration-1000 ease-out" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(20px)",
    transitionDelay: `${index * 150}ms`
  }}>
      <div className="text-6xl sm:text-7xl lg:text-8xl font-black text-secondary tracking-tighter drop-shadow-[0_0_15px_rgba(190,90,50,0.2)]">
        <AnimatedCounter value={stat.value} suffix={stat.suffix} isVisible={isVisible} />
      </div>
      <Badge variant="outline" className="px-4 py-1.5 text-xs sm:text-sm uppercase tracking-widest border-secondary/20 text-secondary bg-secondary/5 backdrop-blur-xl shadow-none">
        {stat.label}
      </Badge>
    </div>;
}
export default function ImpactInNumbers() {
  return <section id="onze-kracht-in-cijfers" className="dark bg-card text-foreground relative overflow-hidden py-24 sm:py-32 border-y border-white/5">
      {}
      <div className="absolute inset-0 opacity-10 pointer-events-none" aria-hidden="true">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid-pattern" width="60" height="60" patternUnits="userSpaceOnUse">
              {}
              <path d="M60 0H0V60H60V0ZM30 0V60M0 30H60" stroke="currentColor" strokeWidth="0.5" fill="none" />
              {}
              <rect x="5" y="5" width="15" height="15" stroke="currentColor" strokeWidth="0.2" fill="none" />
              <rect x="35" y="35" width="15" height="15" stroke="currentColor" strokeWidth="0.2" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid-pattern)" />
        </svg>
        
        {}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,var(--card)_90%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {stats.map((stat, index) => <StatItem key={index} stat={stat} index={index} />)}
        </div>
      </div>
    </section>;
}